import { Nav, Tab } from "react-bootstrap";
import Title from "../../../../../components/Title";
import { pluralize } from "../../../../../utils";
import SearchLink from "../../../../search/components/links/SearchLink";
import { StaticAnalysisEntry } from "../../../@types/rldynamicanalysis/staticanalysis";
import { DYNAMIC_ANALYSIS_EMPTY_MESSAGES } from "../consts";
import CertificateTable from "./ApkCertificateTable";
import CollapsibleTable from "./ApkCollapsibleTable";
import styles from "./rlcscloudsandboxapk.module.scss";

interface Props {
  data?: StaticAnalysisEntry[];
  reportTitle: string;
}

const RlCloudSandboxApk = ({ data = [], reportTitle }: Props) => {
  if (!data?.length)
    return <p className="m-3">{DYNAMIC_ANALYSIS_EMPTY_MESSAGES.apk.no_data}</p>;

  return (
    <div>
      <Title Level="h2" text={`APK (${reportTitle})`} />

      <Tab.Container defaultActiveKey={data[0].package_name}>
        <Nav variant="tabs" className="mb-3">
          {data.map(entry => (
            <Nav.Item key={entry.package_name}>
              <Nav.Link eventKey={entry.package_name}>
                {entry.package_name}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Tab.Content>
          {data.map(entry => (
            <Tab.Pane
              eventKey={entry.package_name}
              key={entry.package_name}
              mountOnEnter
              unmountOnExit
            >
              <div className={styles.apk_details_grid}>
                <div>
                  <b>Label:</b>
                  <span>{entry.label}</span>
                </div>
                <div>
                  <b>Package:</b>
                  <SearchLink
                    value={entry.package_name}
                    keyword="ANDROID_PACKAGE"
                    variant="navigation"
                  />
                </div>
                <div>
                  <b>Version Name:</b>
                  <span>{entry.version_name}</span>
                </div>
                <div>
                  <b>Version Code:</b>
                  <span>{entry.version_code}</span>
                </div>
                <div>
                  <b>Min SDK:</b>
                  <span>{entry.minimum_sdk}</span>
                </div>
                <div>
                  <b>Target SDK:</b>
                  <span>{entry.target_sdk}</span>
                </div>
              </div>
              <div className={styles.apk_accordion_group}>
                <CollapsibleTable
                  title={tableData => `Permissions (${tableData.length})`}
                  data={entry.permissions || []}
                  columns={[
                    {
                      key: "permission",
                      title: "Permission",
                      render: permission => (
                        <SearchLink
                          value={permission}
                          keyword="ANDROID_PERMISSION"
                          variant="navigation"
                        />
                      )
                    }
                  ]}
                  filterPredicate={(permission, filter) =>
                    permission.toLowerCase().includes(filter.toLowerCase())
                  }
                  filterPlaceholder={"Search permissions..."}
                />

                <CollapsibleTable
                  title={tableData => `Activities (${tableData.length})`}
                  data={entry.activities || []}
                  columns={[
                    {
                      key: "activity",
                      title: "Activity",
                      render: row => row.name
                    }
                  ]}
                  filterPredicate={(row, filter) =>
                    row.name.toLowerCase().includes(filter.toLowerCase())
                  }
                  filterPlaceholder={"Search activities..."}
                />

                <CollapsibleTable
                  title={tableData => `Services (${tableData.length})`}
                  data={entry.services || []}
                  columns={[
                    {
                      key: "service",
                      title: "Service",
                      render: row => row.name
                    },
                    {
                      key: "intents",
                      title: "Intents",
                      render: row => row.intents?.join(", ") || "--"
                    }
                  ]}
                  filterPredicate={(row, filter) =>
                    row.name.toLowerCase().includes(filter.toLowerCase()) ||
                    !!row.intents
                      ?.join(", ")
                      .toLowerCase()
                      .includes(filter.toLowerCase())
                  }
                  filterPlaceholder={"Search services..."}
                />

                <CollapsibleTable
                  title={tableData => `Receivers (${tableData.length})`}
                  data={entry.receivers || []}
                  columns={[
                    {
                      key: "receiver",
                      title: "Receiver",
                      render: row => row.name
                    },
                    {
                      key: "intents",
                      title: "Intents",
                      render: row => row.intents?.join(", ") || "--"
                    }
                  ]}
                  filterPredicate={(row, filter) =>
                    row.name.toLowerCase().includes(filter.toLowerCase()) ||
                    !!row.intents
                      ?.join(", ")
                      .toLowerCase()
                      .includes(filter.toLowerCase())
                  }
                  filterPlaceholder={"Search receivers..."}
                />

                <CollapsibleTable
                  title={tableData =>
                    `${pluralize("Certificate", tableData.length)}${
                      tableData.length > 1 ? ` (${tableData.length})` : ""
                    }`
                  }
                  data={entry.certificate || []}
                  columns={[]}
                >
                  <CertificateTable certificates={entry.certificate} />
                </CollapsibleTable>
              </div>
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default RlCloudSandboxApk;
